import { Component, DoCheck, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EvaluationService } from '../../evaluation.service';
import { ValidationService } from '../../validation.service';
import { AuthService } from 'src/app/auth.service';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AufnahmePunktDocument, isB, isG, isM } from 'src/app/data-model';
import { isAnhydromorphHbf } from '../../bodenform-hydromorphie';
import { applyEntwaesserungsVorschlag } from '../../evaluators/entwaesserung';
import { applySubstratgruppeVorschlag } from '../../evaluators/substratgruppe';
import { Subject } from 'rxjs';
import { ProjectService } from 'src/app/project.service';
import { verfahrenVon } from '../../verfahren/verfahren';

@Component({
    standalone: false,
    template: '',
    styles: [''],
    
})
export class HlAreaComponent implements OnInit, OnDestroy, DoCheck {
  @Input() sbj = new Subject<any>;
  /**
   * Der Punkt, den das Formular bearbeitet. Gilt als „ab dem ersten Laden immer
   * da" (Nutzer-Entscheidung 19.09.2026): Alle Formulare haengen als Kinder an
   * der Route `bohrpunkte/:id`, und deren Resolver liefert den Punkt, BEVOR der
   * Router das Bauteil erzeugt; `ngOnInit` uebernimmt ihn, bevor die Vorlage das
   * erste Mal gezeichnet wird.
   *
   * Bis dahin stand hier `any` - damit war auch alles darunter typlos, in den
   * Klassen wie in den Vorlagen: Ein Tippfehler in einem Feldnamen fiel
   * nirgends auf. Festgehalten in `hl-area-data-typ.spec.ts`.
   */
  data!: AufnahmePunktDocument;

  vs = inject(ValidationService);
  private aus = inject(AuthService);
  private ps = inject(ProjectService);
  protected es = inject(EvaluationService);
  private ns = inject(NavbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  constructor() {
		this.sbj.subscribe((bp: any) => {
			this.data = bp;
			this.es.setContext(bp);
			this.vs.setContext(bp)
		});
	}

	ngOnInit() {
		this.route.parent?.data.subscribe(data => {
			this.sbj.next(data['bp']);
		});
		this.aus.authChanged.subscribe(() => this.initButtons());
		this.initButtons();
  }
	
	ngOnDestroy() {
		this.ns.unsubscribe(Command.commentTools);
	}

  ngDoCheck(): void {
		this.vs.setValidationState();
		// Die beiden Vorschlaege rechnen nach der SEA (Anlage 2 und die
		// Feinbodenform) und schreiben in SEA-Felder. Bei einer Aufnahme nach der
		// Bodenkundlichen Kartieranleitung gibt es diese Felder nicht; sie zu
		// fuellen hiesse, in einen Datensatz zu schreiben, der so nie aufgenommen
		// wurde. Festgehalten in `verfahren/ka/ka-masken.spec.ts`.
		if (verfahrenVon(this.data) !== 'sea95') return;
		applyEntwaesserungsVorschlag(this.data);
		// Substratgruppe aus der Feinbodenform vorbelegen. Steht hier und nicht in
		// der Boden-Maske, weil die Bodenform auch von woanders kommen kann
		// (Import, Sammelbearbeitung, FBF-Auswahlfenster). Beide Vorschlaege
		// fassen einen von Hand gesetzten Wert nicht an.
		applySubstratgruppeVorschlag(this.data);
	}

	initButtons() {
		if (this.ps.canI('write', 'comments')) {
			// commenting/pen werden vom Per-Punkt-Modus gesteuert
			// (siehe bohrpunkt-tabs + PointEditModeService). Hier nur noch
			// das Toolbar-Subscribe.
			this.ns.subscribe(Command.commentTools, () => {});
		}
		else {
			this.ns.unsubscribe(Command.commentTools);
		}
	}
	
  onClose() {
    this.router.navigate(['/bohrpunkte', this.data!._id]);
  }

  /**
   * Aufnahme nach SEA 95? Alle Punkte ohne Vermerk sind es (siehe
   * `verfahren/verfahren.ts`). Masken, die auch bei einer Aufnahme nach der
   * Bodenkundlichen Kartieranleitung angeboten werden (heute die
   * Vegetationsaufnahme), lassen ihre forstlichen Teile damit weg.
   */
  get istSeaPunkt(): boolean {
    return verfahrenVon(this.data) === 'sea95';
  }

  isB(): boolean {
    return isB(this.data);
  }

  isG(): boolean {
    return isG(this.data);
  }

  isM(): boolean {
    return isM(this.data);
  }

  /** Prüft ob Kartierer/Datum angezeigt werden darf (nicht für Datennutzer und nicht-angemeldete) */
  canSeePersonalData(): boolean {
    return this.aus.isAuthenticated.getValue() && !this.aus.isDatennutzer();
  }

  /**
   * Kontrolleur-/Kommentier-Modus aktiv? Dann zeigt das Formular statt des
   * Punktart-Pickers das alte wd-Feld, das als Kommentar-Ziel dient (der Picker
   * ist ein reiner Knopf und kann keinen Feld-Kommentar aufnehmen).
   */
  get commenting(): boolean {
    return this.aus.commenting;
  }

  /**
   * Das (G)-Feld (reliktischer Grundwassereinfluss, `bodenform.reliktG`) darf nur
   * aktiviert werden, wenn die primäre (oberste) Hauptbodenform anhydromorph ist
   * – maßgeblich der SEA-D-Katalog (Gruppe "Anhydromorphe Böden", S.81–249).
   */
  reliktGEnabled(): boolean {
    return isAnhydromorphHbf(this.data?.header?.bodenform?.bf?.[0]?.hbf);
  }

  /**
   * Die (G)-Checkbox ist bedienbar, wenn sie aktiviert werden darf (anhydromorph)
   * ODER bereits gesetzt ist – dann bleibt sie deaktivierbar (abwählbar), kann
   * aber nicht erneut gesetzt werden. Sonst ist sie gesperrt.
   */
  reliktGInteractive(): boolean {
    return this.reliktGEnabled() || !!this.data?.header?.bodenform?.reliktG;
  }
}
